import axios from "axios";
import { terminated } from "redux/reducers/auth";
import store from "redux/store";
import { getToken } from "../utils/common-util";
import {
  isSuccessResponse,
  isUnAuthOrSessionExpire,
  isHttpErrorWithCustomError,
  handleError,
  isMaintenanceTime,
  isHttpError,
  errorPayload,
  isAPIErrorWithoutInfo,
} from "../utils/network";
import { isAbortOnLeaveError } from "components/hooks/useAbortController";
import axiosRetry from "axios-retry";
import { getRuntimeEnv, Routes } from "const";

const axiosConfig = {
  baseURL: getRuntimeEnv("REACT_APP_BASE_API"),
  headers: {
    "Cache-Control": "no-cache, no-store, must-revalidate",
    Pragme: "no-cache",
    Expires: "0",
  },
};

export const MoneyConnectClientAPI = axios.create(axiosConfig);
export const RetryableMoneyConnectClientAPI = axios.create(axiosConfig);

applyInterceptors(MoneyConnectClientAPI);

applyRetryable(RetryableMoneyConnectClientAPI);
applyInterceptors(RetryableMoneyConnectClientAPI);

function applyInterceptors(axiosInstance) {
  axiosInstance.interceptors.request.use(
    (config) => {
      config.headers.Authorization = getToken();
      return config;
    },
    (error) => Promise.reject(error)
  );

  axiosInstance.interceptors.response.use(
    (response) => {
      if (shouldRedirectToTheWaitingPage(response)) {
        const handle = redirectToWaitingPage();
        if (!handle) {
          return Promise.reject(errorPayload(collectionErrorInfoAsMuchAsPossible(response), "ERROR"));
        }
        return;
      }

      if (!(response.data instanceof Blob) && !isSuccessResponse(response)) {
        return Promise.reject(handleError(response));
      }

      return response;
    },
    async (error) => {
      const { response, config } = error;

      const isConnectionTimeout = error?.code === "ECONNABORTED";
      if (!isConnectionTimeout && shouldRedirectToTheWaitingPage(response)) {
        const handle = redirectToWaitingPage();
        if (!handle) {
          return Promise.reject(errorPayload(collectionErrorInfoAsMuchAsPossible(response, error), "ERROR"));
        }
        return;
      }

      if (config?.__retryAttempt > 0) {
        config.__retryAttempt--;
        return Promise.reject(error);
      }

      if (isAbortOnLeaveError(error)) {
        return Promise.reject({ code: error.code, name: error.name, ignore: true, isAborted: true });
      }
      if (isUnAuthOrSessionExpire(response)) {
        redirectToTerminatedPage();
        return Promise.reject({ ignore: true });
      }
      if (isMaintenanceTime(response)) {
        redirectToMaintenancePage();
        return Promise.reject({ ignore: true });
      }
      if (isAPIErrorWithoutInfo(response)) {
        return Promise.reject(errorPayload(error?.message, "API_ERROR_NOINF"));
      }
      if (isHttpError(response, error)) {
        return Promise.reject({
          ...errorPayload(collectionErrorInfoAsMuchAsPossible(response, error), "HTTP_ERROR"),
          isHttpError: true,
        });
      }
      if (isHttpErrorWithCustomError(response)) {
        return Promise.reject(handleError(response));
      }
      return Promise.reject(error);
    }
  );
}

function applyRetryable(axiosInstance) {
  axiosRetry(axiosInstance, {
    retries: 3,
    retryCondition: (error) => {
      return !error?.response || error.response.status !== 200;
    },
    retryDelay: (retryCount) => {
      return retryCount * 1000;
    },
    onRetry: (retryCount, _, requestConfig) => {
      requestConfig.__retryAttempt = retryCount;
    },
  });
}

function redirectToTerminatedPage() {
  store.dispatch(terminated());
}

function redirectToMaintenancePage() {
  window.location.reload(); // true, false
}

//! Support F5 Redirect
function shouldRedirectToTheWaitingPage(response) {
  if (response?.status >= 400) {
    return false;
  }

  const responseUrl = response?.request?.responseURL;
  const isCorsBlocked = !response?.status || !responseUrl;
  const isRedirectSuccess = responseUrl?.indexOf(Routes.LimitPage) > -1;

  return isCorsBlocked || isRedirectSuccess;
}

function redirectToWaitingPage() {
  let handle = false;

  const redirectTo = getRuntimeEnv("REACT_APP_LIMIT_USAGE_REDIRECT_TO");
  if (redirectTo) {
    handle = true;
    window.location.href = redirectTo;
  }

  return handle;
}

function collectionErrorInfoAsMuchAsPossible(response, error) {
  const NO_INF = "NO-INF";
  let errors = [];

  if (axios.isAxiosError(error)) {
    if (error?.code) {
      errors.push("Error Code: " + error.code);
    }

    if (error?.message) {
      errors.push("Error Message: " + error.message);
    }

    // Server responded with a status outside the 2xx range
    errors.push("Response Status: " + (response?.status ?? NO_INF));

    // Request was made, but no response received
    if (!response && error.request) {
      errors.push("No response received");
    }
  } else {
    errors.push("Unexpected error");
  }

  return errors.length ? errors.join("\n") + "\n" : null;
}
